import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";

export interface AutomationConfig {
  aiReviewEnabled: boolean;
  aiReviewAutoApprove: boolean;
  aiReviewThreshold: number; // 0-100
  autoScheduleEnabled: boolean;
  autoScheduleDaysAhead: number; // 1-30
  inboxAiDraftsEnabled: boolean;
  inboxAutoReplyEnabled: boolean; // DMs only
  inboxAutoReplyThreshold: number; // 50-100
  inboxAutoReplyMaxPerConv: number; // 1-20
  // ── Autopilot ──
  autopilotEnabled: boolean;
  autopilotCadenceHours: number; // 6-168
  autopilotLookaheadDays: number; // 1-30
  autopilotMaxPiecesPerRun: number; // 1-10
  autopilotMediaPolicy: "copy_only" | "copy_image";
  autopilotPlatformTargets: Record<string, number>; // posts/week, 1-21
  autopilotPausedAt: string | null; // read-only
  autopilotPauseReason: "publish_failures" | "run_failures" | "manual" | null; // read-only
  // ── Ads Boost ──
  adsEnabled: boolean;
  adsMode: "suggest" | "auto";
  // Prisma Decimal: GET serializes as string; the PUT validator requires a
  // number — normalize with Number(...) when seeding form drafts.
  adsMonthlyBudgetUsd: string | number | null; // null = fail closed, no live boosts
  adsPerBoostBudgetUsd: string | number; // same Decimal wrinkle
  adsMinEngagementRate: number; // 0-1
  adsMinImpressions: number;
  adsEvalWindowHours: number; // 24-168
  adsBoostDurationDays: number; // 1-30
  adsGoal: "engagement" | "traffic" | "awareness" | "video_views";
  adsPausedAt: string | null; // read-only
  adsPauseReason: "manual" | "budget" | "provider_errors" | null; // read-only
}

/**
 * PUT body: a partial allowlist plus write-only action flags. Action flags
 * (resumeAutopilot / pauseAds / resumeAds) must be sent as dedicated
 * single-field mutations — never bundled with a form-draft save.
 */
export type AutomationConfigUpdate = Partial<AutomationConfig> & {
  resumeAutopilot?: true;
  pauseAds?: true;
  resumeAds?: true;
};

export function useAutomationConfig(brandId: string) {
  return useQuery({
    queryKey: ["automation-config", brandId],
    queryFn: () => apiFetch<AutomationConfig>(`/brands/${brandId}/automation-config`),
    enabled: !!brandId,
  });
}

export function useUpdateAutomationConfig(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (config: AutomationConfigUpdate) =>
      apiFetch<AutomationConfig>(`/brands/${brandId}/automation-config`, {
        method: "PUT",
        body: JSON.stringify(config),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["automation-config", brandId] }),
    // Guards real-money ads settings — a silent failure here is unacceptable.
    // Server messages are user-readable (matches the use-ads convention);
    // call sites only toast success, so this never double-toasts.
    onError: (err) => {
      toast.error(
        err instanceof ApiClientError
          ? err.message
          : "Could not save automation settings. Please try again.",
      );
    },
  });
}
